import React from 'react';

import { logError } from '../../lib/logger';
import { cn } from '../../lib/utils';

// React unmounts the entire root when a render error reaches it uncaught — one bad row
// in one pane turns the whole shell white. Surfaces mount inside this boundary so a
// crash stays scoped to the pane that threw it. Retry re-renders from current state:
// the crashes this guards against are transient by nature (a live query mid-catchup, a
// row that has since replicated), so the second render usually succeeds. The root
// createRoot options already forward caught errors to Sentry; the log here is for the
// local console trail.
export default class SurfaceErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    logError('SurfaceErrorBoundary', `Render crash in ${this.props.surface || 'surface'}`, error, {
      componentStack: errorInfo?.componentStack,
    });
  }

  handleRetry() {
    this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    return (
      <div className={cn('flex size-full min-h-0 flex-1 flex-col items-center justify-center gap-4 p-6', this.props.className)}>
        <span className="text-center font-sans text-[13px]/[18px] text-gray-500 dark:text-neutral-400">
          Something went wrong showing this view.
        </span>
        <button
          type="button"
          onClick={this.handleRetry}
          className="flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-[0.5px] border-white bg-gray-50 px-3.5 text-[13px] leading-[22px] font-medium text-black shadow-[0_4px_16px_0_rgba(0,0,0,0.1)] transition-transform duration-150 active:scale-[0.96] hover:bg-white dark:border-white/10 dark:bg-neutral-900 dark:text-white dark:hover:bg-neutral-800"
        >
          Try again
        </button>
      </div>
    );
  }
}
